import React from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import "./ProgramsBanner.css";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import "./EducationBehaviorDevelopment.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

/* ---- Simple inline icons (no external icon-font dependency) ---- */
const IconLeaf = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M5 21c8 0 14-6 14-14V5h-2C9 5 3 11 3 19v2h2z" />
    <path d="M5 21c4-6 8-9 14-12" />
  </svg>
);

const IconPencil = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M4 20l1-4L16 5l3 3L8 19l-4 1z" />
    <path d="M14 7l3 3" />
  </svg>
);

const IconCap = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M2 9l10-4 10 4-10 4-10-4z" />
    <path d="M6 11v4c0 1.7 2.7 3 6 3s6-1.3 6-3v-4" />
    <path d="M22 9v6" />
  </svg>
);

const IconGroup = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <circle cx="9" cy="8" r="3" />
    <path d="M2.5 20c0-3.5 2.9-6 6.5-6s6.5 2.5 6.5 6" />
    <circle cx="17.5" cy="8.5" r="2.3" />
    <path d="M15.7 14c2.6.4 4.7 2.5 4.7 5.5" />
  </svg>
);

const IconSchool = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M4 21V9l8-5 8 5v12" />
    <path d="M4 21h16" />
    <path d="M10 21v-6h4v6" />
    <path d="M9 4l1.5 2h3L15 4" />
  </svg>
);

const IconClipboard = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <rect x="5" y="4" width="14" height="17" rx="2" />
    <path d="M9 3.5h6a1 1 0 011 1V6H8V4.5a1 1 0 011-1z" />
    <path d="M8.5 11h7M8.5 14.5h7M8.5 18h4" />
  </svg>
);

const IconHeartHand = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M12 8.5c-1-1.6-3.5-1.6-4.3.2-.7 1.6.7 3 4.3 5.8 3.6-2.8 5-4.2 4.3-5.8-.8-1.8-3.3-1.8-4.3-.2z" />
    <path d="M3 17c1.5-1 3-1 4.5 0l3 1.5c1 .5 2 .5 3 0l6.5-3.3" />
  </svg>
);

const IconBook = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M4 5.5C4 4.7 4.7 4 5.5 4H12v16H5.5A1.5 1.5 0 014 18.5v-13z" />
    <path d="M20 5.5c0-.8-.7-1.5-1.5-1.5H12v16h6.5a1.5 1.5 0 001.5-1.5v-13z" />
  </svg>
);

const IconHeartFilled = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" stroke="none">
    <path d="M12 21s-7.5-4.6-10-9.3C.4 8.6 2 5 5.6 5c2 0 3.4 1.1 4.4 2.6C11 6.1 12.4 5 14.4 5 18 5 19.6 8.6 22 11.7 19.5 16.4 12 21 12 21z" />
  </svg>
);

const IconUserPlus = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="9" cy="8" r="3.2" />
    <path d="M2.5 20c0-3.6 2.9-6.2 6.5-6.2s6.5 2.6 6.5 6.2" />
    <path d="M18 8v5M20.5 10.5h-5" />
  </svg>
);

// Renamed component
const EducationBehaviorDevelopment = () => {
  // Data for the initiatives section for cleaner code
  const initiatives = [
    {
      icon: <IconClipboard />,
      text: "Assessment camps with specialist doctors.",
    },
    {
      icon: <IconHeartHand />,
      text: "Distribution of Aids & Appliances to enhance physical abilities.",
    },
    {
      icon: <IconBook />,
      text: "Special education and life-skill training for CWDs.",
    },
    {
      icon: <IconSchool />,
      text: "Facilitating integration of children into general schools.",
    },
    {
      icon: <IconGroup />,
      text: "Counseling for parents and teachers to create a supportive ecosystem.",
    },
  ];

  const highlights = [
    { icon: <IconCap />, text: "Special Education for CWDs" },
    { icon: <IconGroup />, text: "Life-Skill Training" },
    { icon: <IconGroup />, text: "Integration into Mainstream Schools" },
  ];

  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="page-header page-header--ebd">
        <div
          className="page-header__bg"
          style={{
            backgroundImage: "url(/assets/images/sections/EBD.jpg)",
          }}
        ></div>
        <div className="page-header__overlay"></div>
        <div className="shape1 float-bob-x">
          <img src="assets/images/shapes/page-header-shape1.png" alt="" />
        </div>
        <div className="shape2">
          <img src="assets/images/shapes/page-header-shape2.png" alt="" />
        </div>
        <Container>
          <div className="page-header__inner">
            <motion.span
              className="ebd-eyebrow ebd-eyebrow--hero"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="ebd-eyebrow-icon">
                <IconPencil />
              </span>
              Vikash Foundation
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              Education And Behavior Development
            </motion.h2>
            <div className="ebd-heading-underline ebd-heading-underline--hero">
              <IconLeaf />
            </div>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="page-header__text"
            >
              Vikash supports education and behavior development, offering
              tailored learning and guidance to help individuals thrive in
              their personal and professional lives.
            </motion.p>
          </div>
        </Container>
      </section>

      {/* Introduction Section */}
      <section className="py-5 ebd-approach-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-center justify-content-center g-5">
              <motion.div className="col-lg-6" variants={staggerItem}>
                <span className="ebd-eyebrow">
                  <IconLeaf /> Our Approach
                </span>
                <h2 className="fw-bold mb-2" style={{ color: "#006633" }}>
                  Holistic Support for
                  <br />a Brighter Future
                </h2>
                <div className="ebd-heading-underline">
                  <IconLeaf />
                </div>
                <p className="lh-lg">
                  Vikash provides special education and life-skill training to
                  Children With Disabilities (CWDs). Through specialized
                  camps, we offer case assessments and distribute aids &
                  appliances to enhance their abilities. Once a child gains
                  the required skills, we facilitate their integration into
                  general schools, a process where counseling for both
                  parents and teachers plays a crucial role.
                </p>
              </motion.div>
              <motion.div className="col-lg-6" variants={staggerItem}>
                <div className="ebd-image-card">
                  <img
                    src="/assets/images/Subsections/edc.jpg"
                    alt="Child receiving special education"
                    className="img-fluid rounded-5 shadow"
                  />
                  <div className="ebd-image-strip">
                    {highlights.map((h, idx) => (
                      <div className="ebd-image-strip__item" key={idx}>
                        <span className="ebd-image-strip__icon">{h.icon}</span>
                        <span>{h.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Key Initiatives Section */}
      <section className="py-5 bg-light ebd-programs-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="text-center mb-5"
          >
            <span className="ebd-eyebrow ebd-eyebrow--center">
              <span className="ebd-eyebrow-line"></span>
              <IconLeaf /> Our Core Programs
              <span className="ebd-eyebrow-line"></span>
            </span>
            <h2 className="fw-bold" style={{ color: "#16341f" }}>
              Our Core Programs
            </h2>
            <p className="lead ebd-subtext">
              We focus on a comprehensive approach to support children with
              disabilities and their families.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4 justify-content-center">
              {initiatives.map((initiative, index) => (
                <Col md={6} lg={4} key={index}>
                  <motion.div variants={staggerItem} className="h-100">
                    <Card className="p-4 border-0 shadow-sm h-100 ebd-program-card">
                      <Card.Body className="d-flex align-items-start gap-3 p-0">
                        <span className="ebd-program-icon">{initiative.icon}</span>
                        <div>
                          <p className="mb-0">{initiative.text}</p>
                          <span className="ebd-program-dash"></span>
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Call To Action Section */}
      <section className="py-5 ebd-cta-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <div className="ebd-cta-box">
              <div className="ebd-cta-leaf">
                <IconLeaf />
              </div>
              <div className="ebd-cta-content">
                <div className="ebd-cta-icon">
                  <IconHeartFilled />
                </div>
                <span className="ebd-eyebrow">Make A Difference</span>
                <h2 className="fw-bold mb-2" style={{ color: "#16341f" }}>
                  Support Holistic Development
                </h2>
                <div className="ebd-heading-underline">
                  <IconLeaf />
                </div>
                <p className="mb-4">
                  Your contribution helps us provide specialized education,
                  therapy, and essential aids to children with disabilities.
                </p>
                <div className="ebd-cta-buttons">
                  <Button className="ebd-btn ebd-btn--solid" onClick={openDonation}>
                    <IconHeartFilled /> Donate
                  </Button>
                  <Button
                    className="ebd-btn ebd-btn--outline"
                    onClick={() => navigate("/interns")}
                  >
                    <IconUserPlus /> Join Us
                  </Button>
                </div>
              </div>
              <div className="ebd-cta-image">
                <img
                  src="/assets/images/backgrounds/do3.png"
                  alt="Child in class"
                />
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default EducationBehaviorDevelopment;